import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useMathJax } from "../../../hooks/useMathJax";
import { processText, selectionToMarkdown } from "../../../utils/learnUtils";
import { useLearnCtx } from ".././LearnPage";
import {
  getFlatText,
  getRangeStartOffset,
  findBestMatch,
  clearHighlightsFromDOM,
  applyHighlightToDOM,
  makeHighlightRecord,
  captureSelectionWithLatex,
} from "../../../utils/highlightUtils";
import SelectionToolbar from "./SelectionToolbar";

/* Walk the DOM tree to find the first / last text node within an element */
function getFirstTextNode(el) {
  const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  return w.nextNode();
}

function getLastTextNode(el) {
  const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let last = null,
    node;
  while ((node = w.nextNode())) last = node;
  return last;
}

/**
 * Renders an EDCOMP_PARAGRAPH content item.
 * Supports text selection → toolbar → highlight capture.
 * Stored highlights are re-applied after each MathJax typeset cycle.
 * @param {{ item: object }} props
 */
export default function ParagraphComp({
  item,
  onExplain,
  onAskAI,
  disableSelectionToolbar = false,
}) {
  const { openBookmarkModal, highlights, saveHighlight, deleteHighlight } =
    useLearnCtx();

  const { text, paragraph_title } = item.data ?? {};

  const ref = useRef(null);
  const selectionStartedHere = useRef(false);
  const isClamping = useRef(false);
  const [toolbarPos, setToolbarPos] = useState(null); // { x, y } viewport coords | null
  const [copyDone, setCopyDone] = useState(false);

  // Flat-text offsets of the selection captured when the toolbar last appeared.
  // Both handlers read from here so they don't depend on getSelection() still being
  // active at click time (browsers sometimes collapse it before onClick fires).
  const capturedSelRef = useRef(null); // { start, end, text } | null

  /* ── Highlights for this paragraph only ── */
  const paraHighlights = useMemo(
    () => highlights.filter((h) => h.paragraphId === item._id),
    [highlights, item._id],
  );

  // Always-current reference to this paragraph's highlights, used in the remove
  // handler without adding paraHighlights to its useCallback deps.
  const paraHighlightsRef = useRef(paraHighlights);
  paraHighlightsRef.current = paraHighlights;

  /* ── MathJax + highlight restoration ── */

  // typesetVersion increments each time MathJax finishes; the highlight
  // effect depends on it so marks are inserted after the DOM is fully typeset.
  const [typesetVersion, setTypesetVersion] = useState(0);
  const onTypesetRef = useRef(null);
  onTypesetRef.current = () => setTypesetVersion((v) => v + 1);

  useMathJax(ref, [text], onTypesetRef);

  // Build a stable key so we only clear+re-apply when this paragraph's
  // highlights actually change (not when other paragraphs change).
  const highlightsKey = paraHighlights
    .map((h) => `${h.id}:${h.color}`)
    .join("|");

  useEffect(() => {
    const container = ref.current;
    if (!container || typesetVersion === 0) return;
    clearHighlightsFromDOM(container);
    for (const record of paraHighlights) {
      applyHighlightToDOM(container, record);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typesetVersion, highlightsKey, deleteHighlight]);

  /* ── Add a highlight, merging / trimming any existing overlapping highlights ──
   *
   * Same colour + overlap  → extend the new highlight to the union of both ranges.
   * Diff colour + overlap  → trim the existing highlight to the non-overlapping
   *                          portion(s), then lay the new highlight on top.
   */
  const handleHighlight = useCallback(
    (color) => {
      const captured = capturedSelRef.current;
      if (!captured || !ref.current) return;
      if (!captured.text.trim()) return;
      const flat = getFlatText(ref.current);

      // Resolve existing highlights against the new selection.
      // Sort left-to-right so same-colour rightward merges accumulate correctly.
      let newStart = captured.start;
      let newEnd = captured.end;

      const sorted = paraHighlightsRef.current
        .map((r) => {
          const hStart = findBestMatch(flat, r.exact, r.prefix, r.suffix);
          return hStart === -1
            ? null
            : { record: r, hStart, hEnd: hStart + r.exact.length };
        })
        .filter(Boolean)
        .sort((a, b) => a.hStart - b.hStart);

      for (const { record, hStart, hEnd } of sorted) {
        if (hEnd <= newStart || hStart >= newEnd) continue; // no overlap

        deleteHighlight(record.id);

        if (record.color === color) {
          // Same colour: absorb into the growing extent
          newStart = Math.min(newStart, hStart);
          newEnd = Math.max(newEnd, hEnd);
        } else {
          // Different colour: keep any non-overlapping piece(s) of the old highlight
          if (hStart < newStart)
            saveHighlight(makeHighlightRecord(record, flat, hStart, newStart));
          if (hEnd > newEnd)
            saveHighlight(makeHighlightRecord(record, flat, newEnd, hEnd));
        }
      }

      // Create the single final highlight (possibly extended by same-colour merging)
      saveHighlight({
        paragraphId: item._id,
        color,
        exact: flat.slice(newStart, newEnd),
        prefix: flat.slice(Math.max(0, newStart - 40), newStart),
        suffix: flat.slice(newEnd, newEnd + 40),
      });

      window.getSelection()?.removeAllRanges();
      capturedSelRef.current = null;
      setToolbarPos(null);
    },
    [item._id, saveHighlight, deleteHighlight],
  );

  /* ── Partial / full removal of highlights overlapping the captured selection ──
   *
   * Given highlight [hStart, hEnd) and selection [selStart, selEnd):
   *   (i)   sel covers whole highlight  → delete
   *   (ii)  sel clips only the start    → shrink to [selEnd, hEnd)
   *   (iii) sel clips only the end      → shrink to [hStart, selStart)
   *   (iv)  sel is inside highlight     → split into [hStart,selStart) + [selEnd,hEnd)
   */
  const handleRemoveHighlight = useCallback(() => {
    const captured = capturedSelRef.current;
    if (!captured || !ref.current) return;
    const { start: selStart, end: selEnd } = captured;
    const flat = getFlatText(ref.current);

    const makeRecord = (base, newStart, newEnd) => ({
      paragraphId: base.paragraphId,
      color: base.color,
      exact: flat.slice(newStart, newEnd),
      prefix: flat.slice(Math.max(0, newStart - 40), newStart),
      suffix: flat.slice(newEnd, newEnd + 40),
    });

    for (const record of paraHighlightsRef.current) {
      const hStart = findBestMatch(
        flat,
        record.exact,
        record.prefix,
        record.suffix,
      );
      if (hStart === -1) continue;
      const hEnd = hStart + record.exact.length;

      if (hEnd <= selStart || hStart >= selEnd) continue; // no overlap

      deleteHighlight(record.id);

      const clipsStart = selStart <= hStart;
      const clipsEnd = selEnd >= hEnd;

      if (clipsStart && clipsEnd) {
        // (i) whole highlight selected — already deleted, nothing to re-add
      } else if (clipsStart) {
        // (ii) sel clips start: keep [selEnd, hEnd)
        saveHighlight(makeRecord(record, selEnd, hEnd));
      } else if (clipsEnd) {
        // (iii) sel clips end: keep [hStart, selStart)
        saveHighlight(makeRecord(record, hStart, selStart));
      } else {
        // (iv) sel is inside: split into left + right pieces
        saveHighlight(makeRecord(record, hStart, selStart));
        saveHighlight(makeRecord(record, selEnd, hEnd));
      }
    }

    window.getSelection()?.removeAllRanges();
    capturedSelRef.current = null;
    setToolbarPos(null);
  }, [deleteHighlight, saveHighlight]);

  /* ── Selection tracking ── */

  const handlePointerDown = useCallback(() => {
    selectionStartedHere.current = true;
  }, []);

  useEffect(() => {
    const reset = () => {
      selectionStartedHere.current = false;
    };
    document.addEventListener("pointerup", reset);
    return () => document.removeEventListener("pointerup", reset);
  }, []);

  /* Clamp selection to paragraph boundary + manage toolbar position */
  useEffect(() => {
    const onSelectionChange = () => {
      if (isClamping.current) return;

      const sel = window.getSelection();
      const container = ref.current;
      if (
        !sel ||
        !sel.anchorNode ||
        !sel.focusNode ||
        sel.rangeCount === 0 ||
        !container
      ) {
        setToolbarPos(null);
        return;
      }

      const anchorInside = container.contains(sel.anchorNode);
      const focusInside = container.contains(sel.focusNode);

      if (selectionStartedHere.current && anchorInside && !focusInside) {
        isClamping.current = true;

        const focusBeforeUs = !!(
          container.compareDocumentPosition(sel.focusNode) &
          Node.DOCUMENT_POSITION_PRECEDING
        );

        if (focusBeforeUs) {
          const first = getFirstTextNode(container);
          if (first)
            sel.setBaseAndExtent(sel.anchorNode, sel.anchorOffset, first, 0);
        } else {
          const last = getLastTextNode(container);
          if (last)
            sel.setBaseAndExtent(
              sel.anchorNode,
              sel.anchorOffset,
              last,
              last.length,
            );
        }

        isClamping.current = false;
        return;
      }

      if (sel.isCollapsed || !anchorInside || !focusInside) {
        capturedSelRef.current = null;
        setToolbarPos(null);
        return;
      }

      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        setToolbarPos(null);
        return;
      }

      // Capture the selection as flat-text offsets right now, while the
      // selection is guaranteed to be live and intact.
      const selText = sel.toString();
      const startOff = getRangeStartOffset(range, container);
      capturedSelRef.current =
        startOff !== null
          ? {
              start: startOff,
              end: startOff + selText.length,
              text: selText,
              range: range.cloneRange(),
            }
          : null;

      setToolbarPos({ x: rect.left + rect.width / 2, y: rect.top });
    };

    document.addEventListener("selectionchange", onSelectionChange);
    return () =>
      document.removeEventListener("selectionchange", onSelectionChange);
  }, []);

  /* Hide toolbar when the scroll container scrolls */
  useEffect(() => {
    if (!toolbarPos) return;
    const hide = () => setToolbarPos(null);
    window.addEventListener("scroll", hide, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", hide, { capture: true });
  }, [toolbarPos]);

  /* Suppress native mobile context menu (Android long-press toolbar) */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const suppress = (e) => e.preventDefault();
    el.addEventListener("contextmenu", suppress);
    return () => el.removeEventListener("contextmenu", suppress);
  }, []);

  /* ── Copy ── */
  const copySelection = useCallback(async (e) => {
    e.stopPropagation();
    const text = selectionToMarkdown();
    try {
      await navigator.clipboard.writeText(text);
      setCopyDone(true);
      setTimeout(() => setCopyDone(false), 1500);
    } catch {
      /* clipboard access denied */
    }
  }, []);

  const preventDeselect = useCallback((e) => e.preventDefault(), []);

  const handleExplain = useCallback(() => {
    const text = capturedSelRef.current?.text;
    if (!text?.trim() || !onExplain) return;
    setToolbarPos(null);
    capturedSelRef.current = null;
    window.getSelection()?.removeAllRanges();
    onExplain(text);
  }, [onExplain]);

  const handleAskAI = useCallback(() => {
    const captured = capturedSelRef.current;
    if (!captured || !onAskAI) return;
    const text =
      ref.current && captured.range
        ? captureSelectionWithLatex(captured.range, ref.current)
        : captured.text;
    if (!text?.trim()) return;
    setToolbarPos(null);
    capturedSelRef.current = null;
    window.getSelection()?.removeAllRanges();
    onAskAI(text);
  }, [onAskAI]);

  const toolbarStyle = toolbarPos
    ? {
        position: "fixed",
        left: `${toolbarPos.x}px`,
        top: `${toolbarPos.y - 8}px`,
        bottom: "auto",
        transform: "translateX(-50%) translateY(-100%)",
        zIndex: 50,
      }
    : null;

  return (
    <div
      id={"parag:" + String(item._id)}
      ref={ref}
      className="ct__para"
      data-content-id={item._id}
      onPointerDown={handlePointerDown}
    >
      {paragraph_title && (
        <strong className="ct__para-title">{paragraph_title}&nbsp;</strong>
      )}
      <span dangerouslySetInnerHTML={{ __html: processText(text) }} />

      {toolbarPos && !disableSelectionToolbar && (
        <SelectionToolbar
          style={toolbarStyle}
          onCopy={copySelection}
          copyDone={copyDone}
          onPointerDown={preventDeselect}
          onBookmark={() =>
            openBookmarkModal(item._id, item.title, "parag:" + item._id)
          }
          onHighlight={handleHighlight}
          onRemoveHighlight={handleRemoveHighlight}
          onExplain={onExplain ? handleExplain : undefined}
          onAskAI={onAskAI ? handleAskAI : undefined}
        />
      )}
    </div>
  );
}
